import React from 'react';
import { formatCount, formatDaySpan, formatWeekday } from '@/domain/warehouse';
import GroupedDataTable, { type GroupedTableColumn, type GroupedTableGroup } from '../common/GroupedDataTable';
import LibraryPager from '../common/LibraryPager';
import { LibraryRecordCardList, LibraryRecordCardSkeleton } from '../common/LibraryRecordCard';
import ErrorBanner from '../ErrorBanner';
import './LibraryList.css';

export type LibraryListNoun = { singular: string; plural: string };
export type LibraryListFoldCopy = { title: string; body: string };

/** A row the list can key, group and flag; lists without grouping or lateness leave those out. */
export type LibraryListRow = { id: string; groupKey?: string | null; daysLate?: number | null };

/** One server page: the rows plus the full count, and the groups and fold when the list has them. */
export type LibraryListData<Row> = {
  rows: Row[];
  total: number;
  groups?: ReadonlyArray<{ key: string; label: string | null; count: number; fromDay: string | null; toDay: string | null }>;
  fold?: { count: number; included: boolean } | null;
};

type LibraryListBodyProps<Row extends LibraryListRow> = {
  /**
   * The tabpanel's id and the tab that labels it. Leave them out when something around the list is
   * the tabpanel already (LibrarySheet).
   */
  panelId?: string;
  labelledBy?: string;
  noun: LibraryListNoun;
  /** 'grid' draws the cards on wider screens too; every other view draws the table. */
  view: string;
  /** ≤640px: cards instead of the table. */
  isPhone: boolean;
  data: LibraryListData<Row> | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  /** Shown instead of the list when the filters can't match anything (no status picked). */
  blockedMessage?: string | null;
  columns: ReadonlyArray<GroupedTableColumn<Row>>;
  /** Extra class on a table row (e.g. a selected row). */
  rowClassName?: (row: Row) => string | null | undefined;
  grouped: boolean;
  /** Words after a group's label by group key, e.g. drafts → "not sent yet"; dates otherwise. */
  groupHints?: { readonly [key: string]: string };
  renderCard: (row: Row) => React.ReactNode;
  /** The folded backlog's copy; null when folding is off or a quick filter lists it anyway. */
  fold?: LibraryListFoldCopy | null;
  onToggleFold?: () => void;
  emptyTitle: string;
  emptyHint: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  pageNote: string | null;
  /** Replaces the list and pager (the Calendar view). */
  calendar?: React.ReactNode;
};

/** The folded backlog as a card, for phones and the Grid view (the table draws it as its last row). */
function LibraryListFoldCard({ title, body, count, expanded, onToggle }: LibraryListFoldCopy & {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <section className="library-list-fold-card" aria-label={title}>
      <p className="library-list-fold-card__title">
        <strong>{title}</strong>
        <span className="library-list-fold-card__count">{`${formatCount(count)} · ${expanded ? 'shown' : 'collapsed'}`}</span>
      </p>
      <p className="library-list-fold-card__body">{body}</p>
      <button type="button" className="btn xs" aria-expanded={expanded} onClick={onToggle}>
        {expanded ? 'Hide' : 'Show'}
      </button>
    </section>
  );
}

function noop() {}

/**
 * A server-paged library list: the table (or cards on phones and in Grid), its folded backlog, the
 * pager and the error and empty states. Shared by the Warehouse tabs and the Invoicing library.
 */
export default function LibraryListBody<Row extends LibraryListRow>({
  panelId,
  labelledBy,
  noun,
  view,
  isPhone,
  data,
  loading,
  error,
  onRetry,
  blockedMessage,
  columns,
  rowClassName,
  grouped,
  groupHints,
  renderCard,
  fold,
  onToggleFold,
  emptyTitle,
  emptyHint,
  page,
  pageSize,
  onPageChange,
  pageNote,
  calendar,
}: LibraryListBodyProps<Row>) {
  const rows = React.useMemo(() => data?.rows ?? [], [data?.rows]);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The list shrank under the current page (another user closed records): go to its last page.
  React.useEffect(() => {
    if (data && total > 0 && page > pageCount) onPageChange(pageCount);
  }, [data, onPageChange, page, pageCount, total]);

  const groups = React.useMemo<GroupedTableGroup[]>(() => (data?.groups ?? []).map((group) => ({
    key: group.key,
    label: group.label ?? group.key,
    count: group.count,
    hint: groupHints?.[group.key]
      ?? (group.fromDay && group.fromDay === group.toDay ? formatWeekday(group.fromDay) : formatDaySpan(group.fromDay, group.toDay)),
  })), [data?.groups, groupHints]);

  const serverFold = data?.fold ?? null;
  const foldRow = fold && serverFold && serverFold.count > 0
    ? { ...fold, count: serverFold.count, expanded: serverFold.included, onToggle: onToggleFold ?? noop }
    : null;

  const errorMessage = error instanceof Error
    ? error.message
    : error ? `${noun.plural.charAt(0).toUpperCase()}${noun.plural.slice(1)} could not be loaded.` : null;
  const listLabel = `${noun.plural.charAt(0).toUpperCase()}${noun.plural.slice(1)}`;
  const getRowKey = React.useCallback((row: Row) => row.id, []);
  const getGroupKey = React.useCallback((row: Row) => row.groupKey, []);
  const rowTone = React.useCallback((row: Row) => (row.daysLate != null && row.daysLate > 0 ? 'late' as const : null), []);

  let content: React.ReactNode;
  if (blockedMessage) {
    content = (
      <div className="library-list-empty">
        <strong>{blockedMessage}</strong>
      </div>
    );
  } else if (calendar) {
    content = calendar;
  } else if (isPhone || view === 'grid') {
    content = (
      <div className={view === 'grid' && !isPhone ? 'library-list-card-grid' : undefined}>
        {loading && !data ? (
          <LibraryRecordCardList label={`Loading ${noun.plural}`} busy>
            {[0, 1, 2].map((index) => <LibraryRecordCardSkeleton key={index} />)}
          </LibraryRecordCardList>
        ) : rows.length === 0 ? (
          <div className="library-list-empty">
            <strong>{emptyTitle}</strong>
            <span>{emptyHint}</span>
          </div>
        ) : (
          <LibraryRecordCardList label={listLabel}>{rows.map(renderCard)}</LibraryRecordCardList>
        )}
        {foldRow ? <LibraryListFoldCard {...foldRow} /> : null}
      </div>
    );
  } else {
    content = (
      <GroupedDataTable
        ariaLabel={listLabel}
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        getGroupKey={grouped ? getGroupKey : undefined}
        groups={groups}
        rowTone={rowTone}
        rowClassName={rowClassName}
        fold={foldRow}
        emptyState={loading ? `Loading ${noun.plural}…` : emptyTitle}
      />
    );
  }

  return (
    <div className="library-list-body" role={panelId ? 'tabpanel' : undefined} id={panelId} aria-labelledby={panelId ? labelledBy : undefined}>
      {errorMessage ? <ErrorBanner message={errorMessage} onRetry={onRetry} /> : null}
      {content}
      {!blockedMessage && !calendar ? (
        <LibraryPager
          page={Math.min(page, pageCount)}
          pageSize={pageSize}
          total={total}
          note={pageNote}
          onPageChange={onPageChange}
          ariaLabel={`${listLabel} pages`}
        />
      ) : null}
    </div>
  );
}
